import { Button, Space, Typography, List, Tag, Switch, Divider, Tooltip, Alert } from 'antd';
import {
  SettingOutlined,
  DragOutlined,
  EyeOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useMenuStore } from '@shared/store/menuStore';
import { useTranslation } from 'react-i18next';
import { ResponsiveDrawer } from '@shared/components/ResponsiveDrawer';

const { Text } = Typography;

interface MenuSettingsPanelProps {
  compact?: boolean;
}

export function MenuSettingsPanel({ compact = false }: MenuSettingsPanelProps) {
  const { t } = useTranslation(['translation']);
  const [isOpen, setIsOpen] = useState(false);
  const { editMode, hiddenItems, toggleEditMode, showMenuItem, resetMenuSettings } =
    useMenuStore();

  const handleReset = () => {
    if (window.confirm(t('menu_settings.confirm_reset') || 'Сбросить все настройки меню?')) {
      resetMenuSettings();
    }
  };

  return (
    <>
      {/* Кнопка открытия панели настроек */}
      <div className={compact ? 'flex justify-center' : undefined}>
        <Tooltip title={t('menu_settings.title') || 'Настройки меню'}>
          <Button
            type={compact ? (editMode ? 'primary' : 'text') : 'default'}
            size={compact ? 'small' : 'middle'}
            icon={<SettingOutlined />}
            onClick={() => setIsOpen(true)}
            block={!compact}
            shape={compact ? 'circle' : undefined}
            aria-label={t('menu_settings.title') || 'Настройки меню'}
          >
            {!compact && (t('menu_settings.title') || 'Настройки меню')}
          </Button>
        </Tooltip>
      </div>

      {/* Drawer с настройками */}
      <ResponsiveDrawer
        variant="details"
        title={t('menu_settings.title') || 'Настройки меню'}
        placement="left"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        width={360}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          {/* Краткая инструкция */}
          <Alert
            message={t('menu_settings.quick_guide_title') || 'Быстрое руководство'}
            description={
              <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                <Text className="text-xs">
                  <strong>1.</strong> {t('menu_settings.guide_step1') || 'Включите режим редактирования'}
                </Text>
                <Text className="text-xs">
                  <strong>2.</strong> {t('menu_settings.guide_step2') || 'Перетащите пункты меню для изменения порядка'}
                </Text>
                <Text className="text-xs">
                  <strong>3.</strong> {t('menu_settings.guide_step3') || 'Нажмите ПКМ на пункт → "Скрыть из меню"'}
                </Text>
                <Text className="text-xs">
                  <strong>4.</strong> {t('menu_settings.guide_step4') || 'Восстановите скрытые элементы ниже'}
                </Text>
              </Space>
            }
            type="info"
            icon={<InfoCircleOutlined />}
            closable
            className="text-xs"
          />

          {/* Режим редактирования */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Space>
                <DragOutlined />
                <Text strong>
                  {t('menu_settings.edit_mode') || 'Режим редактирования'}
                </Text>
              </Space>
              <Switch checked={editMode} onChange={toggleEditMode} />
            </div>
            <Text type="secondary" className="text-xs">
              {t('menu_settings.edit_mode_hint') ||
                'Включите, чтобы перетаскивать пункты меню и изменять их порядок'}
            </Text>
            {editMode && (
              <Tag color="blue" className="mt-2">
                {t('menu_settings.edit_mode_active') || 'Режим редактирования активен'}
              </Tag>
            )}
          </div>

          <Divider />

          {/* Скрытые элементы */}
          <div>
            <Space className="mb-2">
              <EyeOutlined />
              <Text strong>
                {t('menu_settings.hidden_items') || 'Скрытые элементы'}
              </Text>
              <Tag>{hiddenItems.length}</Tag>
            </Space>

            {hiddenItems.length > 0 ? (
              <>
                <Text type="secondary" className="text-xs block mb-2">
                  {t('menu_settings.hidden_items_hint') ||
                    'Нажмите, чтобы показать элемент в меню'}
                </Text>
                <List
                  size="small"
                  bordered
                  dataSource={hiddenItems}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button
                          key="show"
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => showMenuItem(item)}
                        >
                          {t('common.show') || 'Показать'}
                        </Button>,
                      ]}
                    >
                      <Text ellipsis style={{ maxWidth: '180px' }}>
                        {item}
                      </Text>
                    </List.Item>
                  )}
                />
              </>
            ) : (
              <Text type="secondary" className="text-xs">
                {t('menu_settings.no_hidden_items') || 'Нет скрытых элементов'}
              </Text>
            )}
          </div>

          <Divider />

          {/* Сброс настроек */}
          <div>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Space>
                <ReloadOutlined />
                <Text strong>
                  {t('menu_settings.reset') || 'Сбросить настройки'}
                </Text>
              </Space>
              <Text type="secondary" className="text-xs">
                {t('menu_settings.reset_hint') ||
                  'Вернуть порядок меню и видимость элементов к значениям по умолчанию'}
              </Text>
              <Button danger block icon={<ReloadOutlined />} onClick={handleReset}>
                {t('menu_settings.reset_button') || 'Сбросить все настройки'}
              </Button>
            </Space>
          </div>
        </Space>
      </ResponsiveDrawer>
    </>
  );
}
